import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import './FullSchedule.css';

const WEEKDAYS = ['Ahd', 'Isn', 'Sel', 'Rab', 'Kha', 'Jum', 'Sab'];
const MONTHS = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];

const toLocalISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const FullSchedule = () => {
  const navigate = useNavigate();
  const todayISO = toLocalISODate(new Date());

  const [userData, setUserData] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(`${todayISO.slice(0, 7)}-01T00:00:00`));
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate('/login');
          return;
        }

        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData({ uid: user.uid, ...userDoc.data() });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  useEffect(() => {
    const fetchSchedules = async () => {
      if (!userData?.daerah || !userData?.terminal) return;

      try {
        const schedulesRef = collection(db, 'schedules');
        const q = query(
          schedulesRef,
          where('daerah', '==', userData.daerah),
          where('terminal', '==', userData.terminal),
          where('active', '==', true)
        );

        const querySnapshot = await getDocs(q);
        const schedulesList = [];

        querySnapshot.forEach((scheduleDoc) => {
          schedulesList.push({
            id: scheduleDoc.id,
            ...scheduleDoc.data()
          });
        });

        schedulesList.sort((a, b) => {
          const dateCompare = (a.date || '').localeCompare(b.date || '');
          if (dateCompare !== 0) return dateCompare;
          return (a.startTime || '').localeCompare(b.startTime || '');
        });

        setSchedules(schedulesList);
      } catch (error) {
        console.error('Error fetching schedules:', error);
      }
    };

    fetchSchedules();
  }, [userData]);

  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => {
      const matchType = selectedType === 'all' ? true : schedule.type === selectedType;
      const queryText = searchQuery.trim().toLowerCase();
      const matchSearch = queryText
        ? `${schedule.subject || ''} ${schedule.teacher || ''} ${schedule.type || ''}`.toLowerCase().includes(queryText)
        : true;

      return matchType && matchSearch;
    });
  }, [schedules, searchQuery, selectedType]);

  const schedulesByDate = useMemo(() => {
    const grouped = {};

    filteredSchedules.forEach((schedule) => {
      if (!schedule.date) return;
      if (!grouped[schedule.date]) grouped[schedule.date] = [];
      grouped[schedule.date].push(schedule);
    });

    return grouped;
  }, [filteredSchedules]);

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      const iso = toLocalISODate(date);

      return {
        date,
        iso,
        isCurrentMonth: date.getMonth() === month,
        isToday: iso === todayISO,
        isSelected: iso === selectedDate,
        events: schedulesByDate[iso] || []
      };
    });
  }, [calendarMonth, schedulesByDate, selectedDate, todayISO]);

  const selectedDateEvents = useMemo(() => {
    return schedulesByDate[selectedDate] || [];
  }, [schedulesByDate, selectedDate]);

  const monthLabel = `${MONTHS[calendarMonth.getMonth()]} ${calendarMonth.getFullYear()}`;
  const upcomingCount = filteredSchedules.filter((schedule) => schedule.date && schedule.date >= todayISO).length;

  const moveMonth = (direction) => {
    setCalendarMonth((current) => {
      const next = new Date(current);
      next.setMonth(current.getMonth() + direction);
      return next;
    });
  };

  const goToday = () => {
    const todayMonth = new Date(`${todayISO.slice(0, 7)}-01T00:00:00`);
    setCalendarMonth(todayMonth);
    setSelectedDate(todayISO);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Tarikh tidak tersedia';
    const date = new Date(`${dateStr}T00:00:00`);
    return `${['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'][date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = Number(hours);
    if (Number.isNaN(hour)) return timeStr;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'PlayAndTalk': return 'Play & Talk';
      case 'IVC': return 'Kelas IVC';
      case 'Workshop': return 'Bengkel';
      case 'Quiz': return 'Kuiz';
      case 'Seminar': return 'Seminar';
      default: return type || 'Aktiviti';
    }
  };

  const getTypeClass = (type) => {
    switch (type) {
      case 'PlayAndTalk': return 'play';
      case 'Workshop': return 'workshop';
      case 'Quiz': return 'quiz';
      case 'Seminar': return 'seminar';
      default: return 'ivc';
    }
  };

  const isPastSession = (dateStr) => {
    if (!dateStr) return false;
    return dateStr < todayISO;
  };

  if (loading) {
    return (
      <div className="full-schedule-loading">
        <div className="spinner-large" />
        <p>Memuatkan jadual...</p>
      </div>
    );
  }

  return (
    <div className="full-schedule-container">
      <header className="calendar-topbar">
        <button className="back-btn" type="button" onClick={() => navigate('/dashboard/student')} aria-label="Kembali">
          <span aria-hidden="true" />
        </button>

        <div className="calendar-title">
          <span>Kalendar BTOP Wellness</span>
          <h1>{monthLabel}</h1>
          <p>{userData?.daerah || '-'} / {userData?.terminal || '-'}</p>
        </div>

        <div className="calendar-top-actions">
          <button type="button" onClick={goToday}>Hari ini</button>
          <button type="button" onClick={() => moveMonth(-1)} aria-label="Bulan sebelum">&lt;</button>
          <button type="button" onClick={() => moveMonth(1)} aria-label="Bulan seterusnya">&gt;</button>
        </div>
      </header>

      <section className="calendar-shell">
        <aside className="calendar-side">
          <div className="mini-month-card">
            <div className="mini-month-head">
              <strong>{monthLabel}</strong>
              <span>{filteredSchedules.length} sesi</span>
            </div>
            <div className="mini-stats">
              <div>
                <strong>{upcomingCount}</strong>
                <span>Akan datang</span>
              </div>
              <div>
                <strong>{Object.keys(schedulesByDate).length}</strong>
                <span>Hari kelas</span>
              </div>
            </div>
          </div>

          <div className="calendar-filters">
            <label>Cari kelas</label>
            <input
              type="text"
              placeholder="Subjek atau guru..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />

            <label>Jenis aktiviti</label>
            <select value={selectedType} onChange={(event) => setSelectedType(event.target.value)}>
              <option value="all">Semua jenis</option>
              <option value="IVC">Kelas IVC</option>
              <option value="PlayAndTalk">Play & Talk</option>
              <option value="Workshop">Bengkel</option>
              <option value="Quiz">Kuiz</option>
              <option value="Seminar">Seminar</option>
            </select>
          </div>

          <div className="calendar-legend">
            <span><i className="dot ivc" /> IVC</span>
            <span><i className="dot play" /> Play & Talk</span>
            <span><i className="dot workshop" /> Bengkel</span>
          </div>
        </aside>

        <main className="calendar-main">
          <div className="calendar-weekdays">
            {WEEKDAYS.map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>

          <div className="calendar-grid">
            {calendarDays.map((day) => (
              <button
                key={day.iso}
                type="button"
                className={`calendar-day ${day.isCurrentMonth ? '' : 'muted'} ${day.isToday ? 'today' : ''} ${day.isSelected ? 'selected' : ''}`}
                onClick={() => setSelectedDate(day.iso)}
              >
                <span className="day-number">{day.date.getDate()}</span>
                <div className="day-events">
                  {day.events.slice(0, 3).map((event) => (
                    <span key={event.id} className={`event-chip ${getTypeClass(event.type)}`}>
                      {event.subject || getTypeLabel(event.type)}
                    </span>
                  ))}
                  {day.events.length > 3 && (
                    <span className="event-more">+{day.events.length - 3} lagi</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </main>

        <aside className="event-panel">
          <div className="event-panel-head">
            <span>Tarikh dipilih</span>
            <h2>{formatDate(selectedDate)}</h2>
            <p>{selectedDateEvents.length} kelas / aktiviti</p>
          </div>

          {selectedDateEvents.length === 0 ? (
            <div className="empty-event">
              <strong>Tiada kelas</strong>
              <p>Tak ada kelas yang dijadualkan untuk tarikh ini.</p>
            </div>
          ) : (
            <div className="event-list">
              {selectedDateEvents.map((schedule) => (
                <article key={schedule.id} className={`event-card ${isPastSession(schedule.date) ? 'past' : ''}`}>
                  <div className="event-time">
                    <strong>{formatTime(schedule.startTime)}</strong>
                    <span>{schedule.endTime ? formatTime(schedule.endTime) : 'Tamat belum ada'}</span>
                  </div>

                  <div className="event-body">
                    <div className="event-title-row">
                      <h3>{schedule.subject || 'Subjek'}</h3>
                      <span className={`event-type ${getTypeClass(schedule.type)}`}>
                        {getTypeLabel(schedule.type)}
                      </span>
                    </div>
                    <p>{schedule.teacher || 'Guru belum ditetapkan'}</p>
                    {schedule.room && <p>Bilik: {schedule.room}</p>}
                    {isPastSession(schedule.date) && <span className="event-complete">Selesai</span>}
                  </div>
                </article>
              ))}
            </div>
          )}
        </aside>
      </section>
    </div>
  );
};

export default FullSchedule;
